import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import {
	fetchBuyerAndSupplierUsers,
	fetchBuyerAndSupplierUsersSuccess,
	fetchBuyerAndSupplierUsersFailure,
} from '../actions/work_orders_subscribers_actions';
import { PROJECTS_CRUD_ACTION_CREATORS } from '../actions/projects_actions';

const fetchUtils = new FetchUtils();

// Project subscribers, over /project/projects/subscribers/*. Note the id sits at the
// END of these routes, unlike the walkthrough equivalents.
//
// Server-side semantics worth knowing when reading this: subscribers are derived live
// (creator + every current deliverable/task assignee) until the first manual add or
// remove, which freezes that set into the project's revised*SubscriberEmails. From then
// on the stored list wins, except newly assigned users are still auto-appended, and
// unassignment never removes anyone — only these endpoints do.
const subscribersUrl = (roleType, action, projectId) =>
	`/api/v1/${roleType}/project/projects/subscribers/${action}/${projectId}`;

// Everyone the caller may subscribe: current subscribers, buyer-company users, and the
// internal techs of the project's supplierFacilityIds (all internal teams when the
// project isn't scoped). Buckets are capped at 5 each, so `search` does the real work.
//
// Writes into the shared searched_subscribers slice — the same one SelectUsers reads for
// work orders and walkthroughs — and also resolves the contacts, which is what the note
// input's @-mention autocomplete consumes.
const findProjectSubscribersForRole = (roleType) => (projectId, search) => (dispatch) =>
	new Promise((resolve, reject) => {
		dispatch(fetchBuyerAndSupplierUsers());
		fetchUtils
			.get(getBackendUri() + subscribersUrl(roleType, 'find_subscriber', projectId), { search })
			.then(parseJSON)
			.then(({ json, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (json.status === 'error') {
					dispatch(fetchBuyerAndSupplierUsersFailure([json.message]));
					reject(json.message);
				} else {
					dispatch(fetchBuyerAndSupplierUsersSuccess(json.data));
					resolve(json.data);
				}
			})
			.catch((error) => {
				dispatch(fetchBuyerAndSupplierUsersFailure(error.message));
				reject(error.message);
			});
	});

// add_/remove_ answer with the whole re-hydrated project, so the effective subscriber
// lists land in Redux without a follow-up readOne.
const changeProjectSubscribers = (roleType, action) => (projectId, emails, project) => (dispatch) =>
	new Promise((resolve, reject) => {
		dispatch(PROJECTS_CRUD_ACTION_CREATORS.updateStart(project));
		fetchUtils
			.post(getBackendUri() + subscribersUrl(roleType, action, projectId), {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				data: { emails },
			})
			.then(parseJSON)
			.then(({ json, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (json.status === 'error') {
					dispatch(PROJECTS_CRUD_ACTION_CREATORS.updateError(json.message, project));
					reject(json.message);
				} else {
					dispatch(PROJECTS_CRUD_ACTION_CREATORS.updateSuccess(json.data));
					resolve(json.data);
				}
			})
			.catch((error) => {
				dispatch(PROJECTS_CRUD_ACTION_CREATORS.updateError(error.message, project));
				reject(error.message);
			});
	});

// Role factories rather than pre-bound ForBuyer/ForSupplier pairs (8 exports became
// 5): the caller passes its own userType, so there is no per-call-site ternary to
// pick the wrong one.
export const findProjectSubscribers = findProjectSubscribersForRole;
export const addProjectBuyerSubscribers = (userType) =>
	changeProjectSubscribers(userType, 'add_buyer_subscribers');
export const addProjectSupplierSubscribers = (userType) =>
	changeProjectSubscribers(userType, 'add_supplier_subscribers');
export const removeProjectBuyerSubscribers = (userType) =>
	changeProjectSubscribers(userType, 'remove_buyer_subscribers');
export const removeProjectSupplierSubscribers = (userType) =>
	changeProjectSubscribers(userType, 'remove_supplier_subscribers');
